<script>
    import { client, gql } from "../GraphQL.svelte";
    import { emptyValuation } from "../Contributor.svelte";
    import { FIELDS_CONTENT_FRAGMENT } from "../Participaid.svelte";
    import { isOwnerOfProject } from "../Me.svelte";
    import { closeProject, downloadReport, downloadPersonalReport, settlePeriod } from "../Participaid.svelte";
    import { navigate } from "svelte-routing";
    import { exclamationTriangle } from 'svelte-awesome/icons';

    import Util from "../lib/util.js";
    import FSM from "../lib/fsm.js";
    import ProjectLib from "../models/participaid/Project.js";
    import ProjectField from "../models/participaid/ProjectField.js";
    import ContributorModel from "../models/participaid/Contributor.js";
    import ProjectPageHeader from "../components/ProjectPageHeader.svelte";
    import InviteContributorsDialog from "../components/dialogs/InviteContributorsDialog.svelte";
    import ProjectFieldsDialog from "../components/dialogs/ProjectFieldsDialog2.svelte";
    import Overlay from "../components/Overlay.svelte";
    import ProfilePicture from "../components/ProfilePicture.svelte";
    import TimeAgo from "../components/TimeAgo.svelte";
    import SettingsButton from "../components/buttons/SettingsButton.svelte";
    import Background from "../components/Background.svelte";
    import Notification from "../components/Notification.svelte";
    import Button from "../components/form/Button.svelte";
    import FaIcon from 'svelte-awesome';
    import DescriptionField from "../components/form/DescriptionField.svelte";

    // ParticipAid elements
    import PartiPie from "../components/participaid/PartiPie.svelte";
    import Contributor from "../components/participaid/Contributor.svelte";
    import ContributorList from "../components/participaid/ContributorList.svelte";
    import InviteContributorPlaceholder from "../components/participaid/InviteContributorPlaceholder.svelte";
    import NewEntryDialog from "../components/dialogs/NewEntryDialog.svelte";
    import ReviewStatusSymbol from "../components/participaid/ReviewStatusSymbol.svelte";
    import EntryTags from "../components/form/EntryTags.svelte";
    import ContributionsTable from "../components/participaid/ContributionsTable.svelte";
    import ProjectFinancialsTable from "../components/participaid/ProjectFinancialsTable.svelte";
    import ProjectNotice from "../components/participaid/ProjectNotice.svelte";

    import DataTable, {Head, Body, Row, Cell} from '@smui/data-table';
    import Tab, {Icon, Label} from '@smui/tab';
    import TabBar from '@smui/tab-bar';
    import List, {Group, Item, Graphic, Meta, Separator, Subheader, Text, PrimaryText, SecondaryText} from '@smui/list';

    const iconTabs = [
        {
            icon: 'emoji_people',
            label: 'Participants'
        },
        {
            icon: 'assignment',
            label: 'Entries'
        },
    ];

    export let id;
    export let activeTab = iconTabs[0];

    let colors = Util.randomColors();
    let companies = [];
    let projectValuation;
    let valuations = [];
    let contributors = [];
    let contributions = [];
    let project;
    let myUserId;
    let isOwner;

    let inviteSuccessMsg;
    let successRemoveContributorMsg;

    let showNewEntryDialogForContributor;
    let showProjectConfigDialog;

    $: loaded = valuations.length && contributors.length;
    $: settled = project && project.isSettled;
    $: closed = project && project.isClosed;
    $: opened = !settled && !closed;
    $: showDialog = !!showNewEntryDialogForContributor || !!showProjectConfigDialog;
    $: entryAllowed = isOwner || (!settled && !closed);
    $: isCoWorker = !isOwner;
    $: projectId = !!project && project.id;
    $: hasFields = !!project && ProjectLib.hasFieldsConfigured(project);
    $: isLive = !!project && ProjectLib.isLive(project);

    $: if (activeTab) {
        //navigate(`/p/${id}/${activeTab.label}`, { replace: true });
    }

    console.log(`Initializing Project with colors:`);
    console.log(colors);

    const OVERVIEW_QUERY = gql`
        query($projectId: Guid!) {
            me {
                user { id }
            },
            project(id: $projectId) {
                id,
                title,
                description,
                isSettled,
                isClosed,
                avatarUrl,
                visibilityTeam,
                allowBackTracking,
                companySharePercentage,
                companySharePrice,
                companyValuation,
                stockCalculationStrategy,
                fields {
                    ${FIELDS_CONTENT_FRAGMENT}
                },
                contributors {
                    id,
                    title,
                    hourlySalary,
                    marketAnnualSalary,
                    commissionPercentage,
                    user {
                        id,
                        firstName,
                        lastName,
                        avatarUrl,
                        emailAddress
                    }
                }
            }
        }
    `;

    const OVERVIEW_CONTRIBUTOR_QUERY = gql`
        query {
            me {
                contributingProjects {
                    id,
                    title,
                    isSettled,
                    isClosed,
                    visibilityTeam,
                    allowBackTracking,
                    companySharePercentage,
                    companySharePrice,
                    companyValuation,
                    stockCalculationStrategy,
                    contributors {
                        id,
                        title,
                        hourlySalary,
                        marketAnnualSalary,
                        commissionPercentage,
                        user {
                            id,
                            firstName,
                            lastName,
                            avatarUrl,
                            emailAddress
                        }
                    }
                }
            }
        }
    `;

    const REPORT_DATA_FRAGMENT = `
        percentageSweatEquity,
        pricePerStock,
        hasErrors,
        totalAvailableStocks,
        totalContributionWorth,
        totalOrganisationWorth,
        totalStockNumber,
        undistributedStocks,
        contributorValuations {
            companySharePercentage,
            contributorId,
            contributorName,
            numberOfStocks,
            totalContributionWorth
        }
    `;

    const SUMMARY_QUERY = gql`
        query($projectId: Guid) {
            projectReportData(projectId: $projectId) {
                ${REPORT_DATA_FRAGMENT}
            }
        }
    `;

    const SUMMARY_CONTRIBUTOR_QUERY = gql`
        query($projectId: Guid, $contributorId: Guid) {
            contributorProjectReportData(contributorId: $contributorId, projectId: $projectId) {
                ${REPORT_DATA_FRAGMENT}
            }
        }
    `;

    // return a valuation for every contributor
    function initialiseValuations(_valuations) {
        return contributors.map(contr => valuationForContributor(contr, _valuations))
    }

    function contributorName(contributor) {
        return ContributorModel.fmtName(contributor);
    }

    // get valuation object for contributor from state array or parameter,
    // created default object (empty) if it cannot be found
    function valuationForContributor(contributor, _valuations) {
        let val;

        (_valuations || valuations).forEach(valuation => {
            if (contributor.id == valuation.contributorId) {
                val = valuation;
            }
        });

        // TODO: when you have company shares through another project, is this taken into account?
        let resVal = val || emptyValuation(contributor);

        return {...resVal, ...{contributorName: contributorName(contributor)}}
    }

    function closeDialogs() {
        showNewEntryDialogForContributor = null;
        showProjectConfigDialog = null;
    }

    function handleShowNewEntryDialog(evt) {
        showNewEntryDialogForContributor = evt.detail;
    }

    function handleShowProjectFieldsDialog() {
        showProjectConfigDialog = true;
    }

    function handleNewEntry(evt) {
        let entry = evt.detail;

        console.log("[Project] got notified of new entry");
        console.log(entry);

        closeDialogs();

        // TODO: instead of reloading, add entry to colection and just recalculate the pies
        reload();
    }

    function handleCloseAndSettle() {
        return closeProject(id)
            .then(res => {
                console.log("[Project] successfully closed and settled project");
                console.log(res);

                project = {...project, ...res.data.closeAndSettleProject};
            })
    }

    function handleInvite(evt) {
        inviteSuccessMsg = "Successfully invited contributor";
        reload();
    }

    function handleSettlePeriod() {

    }

    function handleDownloadPersonalReport() {

    }

    function handleDownloadReport() {
        window.open(`https://evalueaid.netlify.app/api/v1/reports/pdf/${id}?token=${localStorage.getItem('token')}`, '_blank');
    }

    function handleContributorDeletion(contr) {
        console.log("contributor deleted from project:");
        console.log(contr);
        successRemoveContributorMsg = "Successfully removed contributor";
        reload();
    }

    function handleLoadedProject(pr) {
        project = pr;
        contributors = project.contributors;

        // go over all contributors, check which one is mine, and check if its the project owner
        // TODO: replace this operation with specific endpoint
        isOwner = isOwnerOfProject(myUserId, {contributors});
        console.log(`Is project owner: ${isOwner}`);
    }

    function reloadFields() {
        ProjectLib.fields(id)
            .then(fields => {
                project = {...project, ...{fields}};
                console.info("reloaded project fields");
            })
            .catch(err =>
                console.error(err)
            )
    }

    function handleLive() {
        showProjectConfigDialog = false;
        reload();
    }

    function reloadValuations() {
        // TODO: refactor the logic of SUMMARY queries
        // into one abstract unit so we can get the project valuation with single
        // endpoint without having to query the project first
    }

    // TODO: abstract this away in lib
    function reload()
    {
        // first request high-level summary
        let requestProject = client.query({
            query: OVERVIEW_QUERY,
            fetchPolicy: "network-only",
            variables: {
                projectId: id
            }
        })

        // then get project overview
        .then(res => {
            myUserId = res.data.me.user.id;

            // this is what we expect would work.
            // however, it only works for project owner, see:
            // https://gitlab.com/eyevestor/evaluaid/app/-/issues/81 and..
            // https://gitlab.com/eyevestor/evaluaid/app/-/issues/84
            if (res.data.me && res.data.project) {
                console.log(`successfully retrieved project`);
                console.log(res);

                handleLoadedProject(res.data.project);

                return client.query({
                    query: SUMMARY_QUERY,
                    fetchPolicy: "network-only",
                    variables: {
                        projectId: id
                    }
                })
            }

            // no project() access for contributors. should be fixed soon.
            // we now have to take a detour to get the same data
            else {
                console.log(`Did not receive Project. Are we a contributor?`);
                console.log(res);

                isOwner = false;

                return client.query({
                    query: OVERVIEW_CONTRIBUTOR_QUERY,
                    fetchPolicy: "network-only",
                    variables: {
                        projectId: id
                    }
                })

                // filter projects and set to the right one
                .then(res => {
                    console.log(`received Project response as contributor:`);
                    console.log(res);

                    res.data.me.contributingProjects.forEach(cp => {
                        if (cp.id == id) {
                            handleLoadedProject(cp);
                        }
                    });

                    return client.query({
                        query: SUMMARY_CONTRIBUTOR_QUERY,
                        fetchPolicy: "network-only",
                        variables: {
                            projectId: id,
                            contributorId: ProjectLib.myContributor(project, myUserId).id
                        }
                    })
                })
            }
        })

        // then get details
        .then(res => {
            console.log(`received Project valuations:`);
            console.log(res);

            //projectValuation = res.data[isOwner ? "projectReportData" : "contributorProjectReportData"];
            projectValuation = res.data["projectReportData"] || res.data["contributorProjectReportData"];

            // find all missing valuations for contributors, initialize
            // defaults, and set result to valuations[] state variable,
            // which should cause (re)rendering of the chart
            valuations = initialiseValuations( projectValuation.contributorValuations );
        })
    }

    reload();

    $: if (id) {
        console.log(`[Project] id changed to ${id}`);
        reload();
    }

</script>

<svelte:head>
    {#if project}
        <title>Participaid Partipie '{project.title}' | Evalueaid</title>
    {:else}
        <title>Participaid project by Evalueaid</title>
    {/if}
</svelte:head>

<div id="page-project" class:settled class:closed class:isOwner>
    <Background content="random" />
    <ProjectPageHeader {project}>
        <slot slot="project-logo-after">

        </slot>
        <span slot="project-switcher-actions">
<!--            <SettingsButton-->
<!--                    class="project-settings-button"-->
<!--                    onlyIcon-->
<!--                    on:click={() => showProjectConfigDialog = true}-->
<!--            />-->
            <Button style="margin-left: 10px;" on:click={handleShowProjectFieldsDialog}>
                {#if isLive || settled || closed || !isOwner}
                    View agreements
                {:else}
                    Configure agreements...
                {/if}
            </Button>
        </span>
    </ProjectPageHeader>

    <ProjectNotice {settled} {closed} {isLive} on:openProjectFields={handleShowProjectFieldsDialog}/>

    <section class="landing">
        <div class="left">
            <ProjectFinancialsTable model={projectValuation} />
        </div>
        <div class="middle" style="width: 400px;">
<!--            {@debug valuations}-->
            <PartiPie {valuations} {colors}/>
        </div>
        <div class="right">
            {#if project && project.description}
                <p>
                    <strong>Partipie description: <nbsp/></strong>
                    <DescriptionField editable={isOwner} content={project.description} projectId={project.id}/>
                </p>
            {/if}
            <List class="demo-list" dense>
                {#if isCoWorker}
                    <Item disabled on:SMUI:action={handleDownloadPersonalReport}>
                        <Graphic class="material-icons">edit</Graphic>
                        <Text>Download personal report (coming soon)</Text>
                    </Item>
                {:else}
                    <Item disabled on:SMUI:action={handleDownloadReport}>
                        <Graphic class="material-icons">send</Graphic>
                        <Text>Download project report (coming soon)</Text>
                    </Item>
                {/if}
                <Separator />
                {#if opened}
                    <Item on:SMUI:action={handleCloseAndSettle}>
                        <Graphic class="material-icons">archive</Graphic>
                        <Text>Settle and close project</Text>
                    </Item>
                {/if}
            </List>
        </div>
    </section>

    <div id="below-fold">
        <div class="inner">

            <TabBar tabs={iconTabs} let:tab bind:active={activeTab} style="max-width: 900px; margin: auto; margin-bottom: 50px;">
                <Tab {tab}>
                    <Icon class="material-icons">{tab.icon}</Icon>
                    <Label>{tab.label}</Label>
                </Tab>
            </TabBar>

            {#if activeTab == iconTabs[0]}
                <section class="contributors">
                    <ContributorList
                            {contributors}
                            {project}
                            {projectValuation}
                            {myUserId}
                            {isOwner}
                            {colors}
                            on:invite={handleInvite}
                            on:newEntryDialog={handleShowNewEntryDialog}
                            on:deletion={handleContributorDeletion}
                            on:reinvite={reload}
                    />
                </section>
            {:else}
                <section class="contributions container">
                    <ContributionsTable
                            {project}
                            {myUserId}
                            {contributors}
                            {isOwner}
                            {colors}
                            editable={opened}
                            on:new={handleNewEntry}
                            on:approved={reload}
                    />
                </section>
            {/if}
        </div>
    </div>

    <Overlay show={showDialog} closable={true} on:close={closeDialogs} background="darken" loaderBackground="#fff">
        {#if showNewEntryDialogForContributor}
            <NewEntryDialog
                    {project}
                    {isOwner}
                    targetUserId={showNewEntryDialogForContributor.user.id}
                    on:new={handleNewEntry}
            />
        {/if}

        {#if showProjectConfigDialog}
            <ProjectFieldsDialog
                    show={showProjectConfigDialog}
                    {projectId}
                    {isOwner}
                    saveable={true}
                    {settled} {closed} {isLive}
                    editable={isOwner && !isLive && !settled && !closed}
                    on:live={handleLive}
            />
        {/if}
    </Overlay>
</div>

<Notification type="success" message={inviteSuccessMsg} />
<Notification type="success" message={successRemoveContributorMsg} />

<style>
    #page-project {
        background: #fffe;
        overflow: hidden;
    }

    #below-fold {
        background: #f5f5f5;
    }

    #below-fold .inner {
        position: relative;
        top: -100px;
    }

    :global(html) {
        //background: 0;
    }

    .contributors {
        padding: 0 20px;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .landing {
        display: flex;
    }

    .landing .middle {
        padding-top: 50px;
        padding-bottom: 150px;
        text-align: center;
    }

    .contributions.container {
        text-align: center;
    }

    .landing .left, .landing .right {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-content: normal;
        align-items: center;
        justify-content: center;
    }

    .landing .left .button, .landing .right .button {
        margin: 5px 0;
    }
</style>